import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { forgotPassword, verifyForgotPasswordOTP, resetPassword, generateTempTokenForReset } from '../api/authApi';

const ForgotPasswordModal = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: email, 2: OTP, 3: new password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpId, setOtpId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await forgotPassword(email);

      if (result.success) {
        // Store otp_id for OTP verification
        if (result.otp_id) {
          setOtpId(result.otp_id);
        }
        setSuccess('Reset OTP sent to your email. Please check your inbox.');
        setStep(2);
      } else {
        setError(result.message || 'Failed to send OTP. Please try again.');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const normEmail = (email || '').trim().toLowerCase();
      const result = await verifyForgotPasswordOTP(otpId, otp, normEmail);

      if (result.success) {
        // Try to generate temp token immediately after OTP verification with small backoff retries
        const tryGenerateToken = async (attempts = 3, delayMs = 600) => {
          for (let i = 0; i < attempts; i++) {
            if (i > 0) {
              await new Promise(resolve => setTimeout(resolve, delayMs));
            }
            try {
              const tokenRes = await generateTempTokenForReset(normEmail, otpId, otp);
              if (tokenRes.success && tokenRes.token) {
                return tokenRes.token;
              }
            } catch (e) {
              console.warn('Temp token attempt failed:', e);
            }
          }
          return null;
        };

          const token = await tryGenerateToken();
        if (token) {
          setResetToken(token);
          setSuccess('OTP verified! Please enter your new password.');
          setStep(3);
        } else {
          // Show backend message if available from a single direct call
          try {
            const once = await generateTempTokenForReset(normEmail);
            setError(once.message || 'Could not generate reset token. Please wait a moment and try again or resend OTP.');
          } catch (e) {
            setError('Could not generate reset token. Please wait a moment and try again or resend OTP.');
          }
        }
      } else {
        setError(result.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const normEmail = (email || '').trim().toLowerCase();
      // Ensure we have a token; if not, try to generate with retries
      let tokenToUse = resetToken;
      if (!tokenToUse) {
        const tryGenerateToken = async (attempts = 2, delayMs = 600) => {
          for (let i = 0; i < attempts; i++) {
            if (i > 0) {
              await new Promise(resolve => setTimeout(resolve, delayMs));
            }
            const tokenRes = await generateTempTokenForReset(normEmail);
            if (tokenRes.success && tokenRes.token) {
              return tokenRes.token;
            }
          }
          return null;
        };
        tokenToUse = await tryGenerateToken();
        if (tokenToUse) {
          setResetToken(tokenToUse);
        }
      }

      // Use email, otp, and token (if available) for password reset
      const result = await resetPassword(normEmail, otp, newPassword, tokenToUse, otpId);

      if (result.success) {
        setSuccess('Password reset successful! You can now login with your new password.');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(result.message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content forgot-password-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h4>Forgot Password</h4>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success" role="alert">
              {success}
            </div>
          )}

          {/* Step 1: Enter Email */}
          {step === 1 && (
            <form onSubmit={handleEmailSubmit}>
              <p className="mb-3">Enter your email address to receive a password reset OTP.</p>
              
              <div className="form-group">
                <input
                  type="email"
                  className="form-control"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => {
                    const val = (e.target.value || '').trim().toLowerCase();
                    setEmail(val);
                    setError('');
                  }}
                  required
                />
              </div>

              <div className="button-box mt-3">
                <button type="submit" disabled={loading}>
                  {loading ? 'Sending...' : 'Send OTP'}
                </button>
              </div>

              <p className="text-center mt-3" style={{fontSize: '12px', color: '#666'}}>
                By proceeding, you agree to our <Link to="/privacy-policy" style={{color: '#007bff', textDecoration: 'none'}}>Privacy Policy</Link>
              </p>
            </form>
          )}

          {/* Step 2: Enter OTP */}
          {step === 2 && (
            <form onSubmit={handleOTPSubmit}>
              <p className="mb-3">
                An OTP has been sent to <strong>{email}</strong>
              </p>

              <div className="form-group">
                <input
                  type="text"
                  className="form-control text-center"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 6) {
                      setOtp(value);
                      setError('');
                    }
                  }}
                  maxLength="6"
                  autoFocus
                />
              </div>

              <div className="button-box mt-3">
                <button type="submit" disabled={loading || otp.length !== 6}>
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </button>
              </div>

              <div className="text-center mt-3">
                <button 
                  type="button" 
                  className="btn-link"
                  onClick={() => {
                    setStep(1);
                    setOtp('');
                    setError('');
                    setSuccess('');
                  }}
                >
                  Use different email
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Reset Password */}
          {step === 3 && (
            <form onSubmit={handlePasswordSubmit}>
              <p className="mb-3">Enter your new password</p>

              <div className="form-group" style={{ position: 'relative' }}>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  className="form-control"
                  placeholder="New Password (min 6 characters)"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setError('');
                  }}
                  required
                  minLength="6"
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  className="btn-password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#666',
                    fontSize: '18px',
                    padding: '5px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {showNewPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>

              <div className="form-group" style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="form-control"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError('');
                  }}
                  required
                  minLength="6"
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  className="btn-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#666',
                    fontSize: '18px',
                    padding: '5px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>

              <div className="button-box mt-3">
                <button type="submit" disabled={loading}>
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .modal-content {
          background: white;
          border-radius: 8px;
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        }

        .modal-header {
          padding: 20px;
          border-bottom: 1px solid #e9ecef;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h4 {
          margin: 0;
          font-size: 24px;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 32px;
          line-height: 1;
          cursor: pointer;
          color: #999;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-btn:hover {
          color: #333;
        }

        .modal-body {
          padding: 30px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-control {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 16px;
        }

        .form-control:focus {
          outline: none;
          border-color: #88c74a;
        }

        .alert {
          padding: 12px;
          margin-bottom: 20px;
          border-radius: 4px;
        }

        .alert-danger {
          background-color: #f8d7da;
          border: 1px solid #f5c6cb;
          color: #721c24;
        }

        .alert-success {
          background-color: #d4edda;
          border: 1px solid #c3e6cb;
          color: #155724;
        }

        .button-box button {
          width: 100%;
          padding: 15px;
          background: #88c74a;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s;
        }

        .button-box button:hover:not(:disabled) {
          background: #75b339;
        }

        .button-box button:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .btn-link {
          background: none;
          border: none;
          color: #88c74a;
          cursor: pointer;
          font-size: 14px;
          text-decoration: underline;
        }

        .btn-link:hover {
          color: #75b339;
        }

        .text-center {
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default ForgotPasswordModal;
